import {isLimited, isVeryLimited} from 'common/report/dashboards/query-concurrency.ts';

import {DEFAULT_QUERY_CONCURRENCY} from '../query-concurrency-scaling';
import {QueryPermitTrackEvent} from './tracking';

export const QUERY_PERMIT_HEARTBEAT_MS = 30_000;
export const QUERY_PERMIT_LIVENESS_THRESHOLD_MS = 60_000;
export const QUERY_PERMIT_LIVENESS_SWEEP_MS = 15_000;
export const QUERY_PERMIT_MAX_HOLD_MS = 90_000;
// How often a tab re-checks a queued request's priority and tells the worker if it changed.
export const QUERY_PERMIT_PRIORITY_POLL_MS = 500;
// Extra slots above the main-query concurrency for the fast requests (query_metadata, upfront
// properties/events), which finish soon enough to share a slot rather than each needing one.
export const QUERY_PERMIT_STANDARD_PAD = 3;
// Constrained backends get less slack: over-provisioning them is riskier.
export const QUERY_PERMIT_LIMITED_PAD = 1;
// A **flat** pad applied when 2 or more tabs are open.
export const QUERY_PERMIT_MULTI_TAB_PAD = 2;
// Max concurrent /api/query/* requests across all tabs of the origin.
export const QUERY_PERMIT_GLOBAL_CAP = DEFAULT_QUERY_CONCURRENCY + QUERY_PERMIT_STANDARD_PAD;
// Reduced ceilings for constrained-backend projects, mirroring the LIMITED / VERY_LIMITED tiers in
// getMaxConcurrentQueries (iron/common/report/dashboards/query-concurrency.ts).
export const QUERY_PERMIT_LIMITED_CAP = 5 + QUERY_PERMIT_LIMITED_PAD;
export const QUERY_PERMIT_VERY_LIMITED_CAP = 2 + QUERY_PERMIT_LIMITED_PAD;

// Fail-open circuit breaker: past this many acquires waiting across all pools, further acquires are
// granted immediately so an unbounded queue can't pin the worker's single thread. This guards
// against our own bug — backend load-shedding stays upstream.
export const QUERY_PERMIT_MAX_PENDING = 1000;

// ---- soft rate throttle ------------------------------------------------------
// Target sustained cross-tab grant rate. The pacer spaces grants at 60000/rpm ms so throughput
// converges to this value.
export const QUERY_PERMIT_SOFT_THROTTLE_RPM = 120;
// Free-burst window, sized to absorb the initial dashboard-load burst. The schedule may run
// (windowMs - interval) ahead of real time before any wait is imposed, so a normal load is not
// throttled. The throttle engages on the (rpm * windowMs / 60000 + 1)th grant within the window.
export const QUERY_PERMIT_SOFT_THROTTLE_WINDOW_MS = 8_000;
// How often the worker forwards each project's aggregated metrics to a live tab for tracking.
export const QUERY_PERMIT_METRICS_FLUSH_MS = 30_000;

// ---- priority / rank ---------------------------------------------------------
//
// Queued acquires are served lowest-rank-first (the worker pool sorts with
// MIN_NODE_COMPARATOR). A rank composes three tiers, in precedence order:
//   1. tab tier        — the foreground (currently-viewed) tab outranks background tabs
//   2. pool tier       — the foreground pool outranks the background (prefetch) pool
//   3. viewport dist   — a viewport distance (px); cards nearer the viewport are served first
// Lower wins at every tier. Tab/pool tiers use FOREGROUND=0 < BACKGROUND=1.
export const FOREGROUND = 0;
export const BACKGROUND = 1;

// Weights make each tier strictly dominate the next: any difference in tab tier
// outweighs every pool tier + viewport-distance combination, and so on. The clamp
// ceiling bounds viewportDistance (which can arrive as Number.MAX_VALUE — the
// sentinel getCardDistanceFromViewport returns for offscreen/unknown cards) so the
// composite stays well under Number.MAX_SAFE_INTEGER.
export const QUERY_PERMIT_VIEWPORT_DISTANCE_MAX = 1_000_000;
export const QUERY_PERMIT_POOL_WEIGHT = QUERY_PERMIT_VIEWPORT_DISTANCE_MAX * 10;
export const QUERY_PERMIT_TAB_WEIGHT = QUERY_PERMIT_POOL_WEIGHT * 10;

export interface PermitRank {
  tabTier: number;
  poolTier: number;
  viewportDistance: number;
}

export enum MessageType {
  // Tab -> worker
  Acquire = `acquire`,
  UpdatePriority = `update-priority`,
  SetTabTier = `set-tab-tier`,
  Cancel = `cancel`,
  Release = `release`,
  Heartbeat = `heartbeat`,
  // Worker -> tab
  Granted = `granted`,
  Reclaimed = `reclaimed`,
  Track = `track`,
}

export interface AcquireMessage {
  type: MessageType.Acquire;
  reqId: string;
  projectId: number;
  softThrottle: boolean;
  poolTier: number;
  viewportDistance: number;
}

export type TabToWorkerMessage =
  | AcquireMessage
  // Re-rank a single queued request (e.g. its card scrolled toward/away from the viewport).
  | {type: MessageType.UpdatePriority; reqId: string; poolTier: number; viewportDistance: number}
  // Re-rank every queued + future request from this port/tab (e.g. the tab was foregrounded).
  | {type: MessageType.SetTabTier; tabTier: number}
  // Withdraw a still-queued acquire that the caller abandoned (e.g. its AbortController
  // fired before the permit was granted), giving up its place in line. No-op once granted.
  | {type: MessageType.Cancel; reqId: string}
  | {type: MessageType.Release; reqId: string}
  | {type: MessageType.Heartbeat};

export type WorkerToTabMessage =
  | {type: MessageType.Granted; reqId: string}
  | {type: MessageType.Reclaimed; reqId: string}
  | ({type: MessageType.Track} & QueryPermitTrackEvent);

export function composeRank({tabTier, poolTier, viewportDistance}: PermitRank): number {
  const clampedDistance = Math.min(Math.max(viewportDistance, 0), QUERY_PERMIT_VIEWPORT_DISTANCE_MAX);
  return tabTier * QUERY_PERMIT_TAB_WEIGHT + poolTier * QUERY_PERMIT_POOL_WEIGHT + clampedDistance;
}

// The ceiling a project's permit pool is sized to, for parity with the per-project dashboard
// concurrency limits (getMaxConcurrentQueries).
export function getQueryPermitGlobalCap(projectId: number | undefined): number {
  if (projectId !== undefined) {
    // The reduced-cap project sets are keyed by the string form of the id.
    const key = String(projectId);
    if (isVeryLimited(key)) {
      return QUERY_PERMIT_VERY_LIMITED_CAP;
    }
    if (isLimited(key)) {
      return QUERY_PERMIT_LIMITED_CAP;
    }
  }
  return QUERY_PERMIT_GLOBAL_CAP;
}
